import endpoints from "constants/api/Endpoints"
import { zeroneReportCategories } from "constants/data/report/ZeroneReportCategories"
import { CategoryTopic } from "interface/CategoryTopic"
import FetchDataByIDProps from "interface/FetchDataByIDProps"
import LegacyChart from "interface/LegacyChart"
import ReportAccess from "interface/ReportAccess"
import ReportCatalog from "interface/ReportCatalog"
import { ReportCategory } from "interface/ReportCategory"
import StripePaymentIntentResult from "interface/StripePaymentIntentResult"
import ZeroneReport from "interface/ZeroneReport"
import { logError } from "services/logService/errorLogger"
import logInfo from "services/logService/infoLogger"
import SimilarReport from "interface/SimilarReport"
import { ReportFilterOptions } from "interface/ReportFilterOptions"

/**
 * Fetches report categories from the API or returns default categories if an error occurs.
 * 
 * @async
 * @function fetchCategories
 * @param {string} tag - A tag used for logging and identifying the request.
 * @returns {Promise<ReportCategory[]>} - A promise that resolves to an array of report categories.
 * 
 * @example
 * async function loadCategories() {
 *   const categories = await fetchCategories('CategoryFetch');
 *   console.log('Fetched categories:', categories);
 * }
 */
export async function fetchCategories(tag: string): Promise<ReportCategory[]>{
    try {
        const result = await endpoints.getReportCategories({ tag })
        if (result.error) {
            logError('Error fetching report categories.', { result }, tag)
            return defaultCategories()
        }
        return result.data ? result.data : defaultCategories()
    } catch (error) {
        logError(
            'Unable to retrieve report categories.',
            { error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return defaultCategories()
    }
}

/**
 * Fetches a specific report category by its ID from the API or returns null if not found.
 * 
 * @async
 * @function fetchCategoryById
 * @param {FetchDataByIDProps} params - An object containing the tag for logging and the ID of the report category to fetch.
 * @returns {Promise<ReportCategory | null>} - A promise that resolves to the fetched report category or `null` if not found.
 * 
 * @example
 * async function loadCategory() {
 *   const category = await fetchCategoryById({ tag: 'CategoryFetch', id: '12345' });
 *   console.log('Fetched category:', category);
 * }
 */
export async function fetchCategoryById({ tag, id }: FetchDataByIDProps): Promise<ReportCategory | null> {
    if (!id) {
        logError('Invalid ID provided', { id }, tag)
        return null
    }
    try {
        const result = await endpoints.getReportCategoryById({ tag, id })
        if (result.success && result.data) {
            return result.data
        }
        logError('Error fetching report category by ID.', { id, result }, tag)
        return null;
    } catch (error) {
        logError(
            'Unable to retrieve report category by ID.',
            { id, error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return null;
    }
}

/**
 * Fetches filter options for a specific report category by its ID from the API or returns empty options if an error occurs.
 * 
 * @async
 * @function fetchReportCategoryFilters
 * @param {FetchDataByIDProps} params - An object containing the tag for logging and the ID of the report category to fetch filters for.
 * @returns {Promise<ReportFilterOptions>} - A promise that resolves to an object containing filter options.
 * 
 * @example
 * async function loadCategoryFilters() {
 *   const filters = await fetchReportCategoryFilters({ tag: 'CategoryFilterFetch', id: '12345' });
 *   console.log('Fetched category filters:', filters);
 * }
 */
export async function fetchReportCategoryFilters({ tag, id }: FetchDataByIDProps): Promise<ReportFilterOptions> {
    if (!id) {
        logError('Invalid ID provided', { id }, tag)
        return { datasetOptions: [], countryOptions: [], reportTypeOptions: [], reportFormatOptions: [] } as ReportFilterOptions
    }
    try {
        const result = await endpoints.getReportCategoryFilters({ tag, id })
        if (result.success && result.data) {
            return result.data
        }
        logError('Error fetching report category filters.', { id, result }, tag)
        return { datasetOptions: [], countryOptions: [], reportTypeOptions: [], reportFormatOptions: [] } as ReportFilterOptions
    } catch (error) {
        logError(
            'Unable to retrieve report category filters.',
            { id, error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return { datasetOptions: [], countryOptions: [], reportTypeOptions: [], reportFormatOptions: [] } as ReportFilterOptions
    }
}

/**
 * Fetches top hot reports (trending reports) from the API or returns default trending reports if an error occurs.
 * 
 * @async
 * @function fetchTopHotReports
 * @param {string} tag - A tag used for logging and identifying the request.
 * @returns {Promise<CategoryTopic[]>} - A promise that resolves to an array of trending reports.
 * 
 * @example
 * async function loadHotReports() {
 *   const hotReports = await fetchTopHotReports('HotReportFetch');
 *   console.log('Fetched trending reports:', hotReports);
 * }
 */
export async function fetchTopHotReports(tag: string): Promise<CategoryTopic[]>{
    try {
        const result = await endpoints.getTopHotReports({ tag })
        if (result.error) {
            logError('Error fetching trending reports.', { result }, tag)
            return []
        }
        return result.data ? result.data : []
    } catch (error) {
        logError(
            'Unable to retrieve trending reports.',
            { error: error  instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return []
    } 
}

/**
 * Fetches a specific report by its ID from the API or returns a default report if an error occurs.
 * 
 * @async
 * @function fetchReport
 * @param {FetchDataByIDProps} params - An object containing the tag for logging and the ID of the report to fetch.
 * @returns {Promise<ReportContent | null>} - A promise that resolves to the fetched report content or `null` if not found.
 * 
 * @example
 * async function loadReport() {
 *   const report = await fetchReport({ tag: 'ReportFetch', id: '12345' });
 *   console.log('Fetched report:', report);
 * }
 */
export async function fetchReport({ tag, id }: FetchDataByIDProps): Promise<ZeroneReport | null> {
    if (!id) {
        logError('Invalid ID provided', { id }, tag)
        return null
    }
    try {
        const result = await endpoints.getReport({ tag, id })
        if (result.success && result.data) {
            return result.data
        }
        logError('Error fetching report.', { id, result }, tag)
        return null;
    } catch (error) {
        logError(
            'Unable to retrieve report.',
            { id, error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return null;
    }
}

/**
 * Fetches a list of reports from the API based on a specific tag.
 * 
 * @async
 * @function fetchReportCatalog
 * @param {string} tag - A tag used for logging and identifying the request.
 * @returns {Promise<ReportCatalog[]>} - A promise that resolves to an array of report catalogs.
 * 
 * @example
 * async function loadReportCatalog() {
 *   const reportCatalog = await fetchReportCatalog('ReportCatalogFetch');
 *   console.log('Fetched report catalog:', reportCatalog);
 * }
 */
export async function fetchReportCatalog(tag: string): Promise<ReportCatalog[]> {
    try {
        const result = await endpoints.getReportCatalog({ tag })
        if (result.error) {
            logError('Error fetching report catalog.', { result }, tag)
            return []
        }
        return result.data ? result.data : []
    } catch (error) {
        logError(
            'Unable to retrieve report catalog.',
            { error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return []
    }
}

/**
 * Checks if the user has access to a specific report by its ID.
 * 
 * @async
 * @function checkAccessToReport
 * @param {FetchDataByIDProps} params - An object containing the tag for logging and the ID of the report to check.
 * @returns {Promise<ReportAccess>} - A promise that resolves to an object containing access information.
 * 
 * @example
 * async function checkAccess() {
 *   const access = await checkAccessToReport({ tag: 'ReportAccess', id: '12345' });
 *   console.log('Access to report:', access);
 * }
 */
export async function checkAccessToReport({ tag, id }: FetchDataByIDProps): Promise<ReportAccess> {
    try {
        const result = await endpoints.checkAccessToReport({ tag, id })
        logInfo('Checking access to report.', { id, result }, tag)
        return result.success && result.data ? result.data : { hasAccess: false, ableToPurchase: false } 
    } catch (error) {
        logError(
            'Unable to check access to report.',
            { id, error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return { hasAccess: false, ableToPurchase: false }
    }
}

/**
 * Downloads a specific report by its ID from the API or returns null if an error occurs.
 * 
 * @async
 * @function downloadReport
 * @param {FetchDataByIDProps} params - An object containing the tag for logging and the ID of the report to download.
 * @returns {Promise<string | null>} - A promise that resolves to the downloaded report content or `null` if not found.
 * 
 * @example
 * async function download() {
 *   const reportContent = await downloadReport({ tag: 'ReportDownload', id: '12345' });
 *   console.log('Downloaded report content:', reportContent);
 * }
 */
export async function downloadReport({ tag, id }: FetchDataByIDProps): Promise<string | null> {
    try {
        const result = await endpoints.downloadReport({ tag, id })
        if (result.success && result.data && typeof result.data.url === 'string' && result.data.url.length > 0) {
            return result.data.url
        }
        logError('Error downloading report.', { id, result }, tag)
        return null
    } catch (error) {
        logError(
            'Unable to download report.',
            { id, error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return null
    }
}

/**
 * Purchases a specific report using credits from the API.
 * 
 * @async
 * @function purchaseReportUsingCredits
 * @param {FetchDataByIDProps} params - An object containing the tag for logging and the ID of the report to purchase.
 * @returns {Promise<{ success: boolean, message: string; creditsUsed: number; url: string }>} - A promise that resolves to an object containing purchase information.
 * 
 * @example
 * async function purchase() {
 *   const purchaseInfo = await purchaseReportUsingCredits({ tag: 'ReportPurchase', id: '12345' });
 *   console.log('Purchase information:', purchaseInfo);
 * }
 */
export async function purchaseReportUsingCredits({ tag, id }: FetchDataByIDProps): Promise<{ success: boolean, message: string; creditsUsed: number; url: string }>{
    try {
        const result = await endpoints.purchaseReportUsingCredits({ tag, id })
        if (result.success && result.data) {
            return { success: true, message: 'Report purchased successfully.', creditsUsed: result.data.creditsUsed, url: result.data.url }
        }

        logError('Error purchasing report.', { id, result }, tag)
        return { success: false, message: 'Error purchasing report.', creditsUsed: 0, url: '' }
    } catch (error) {
        logError(
            'Unable to purchase report.',
            { id, error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return { success: false, message: 'Error purchasing report.', creditsUsed: 0, url: '' }
    }
}

/**
 * Purchases a specific report using payment from the API.
 * 
 * @async
 * @function purchaseReportUsingPayment
 * @param {FetchDataByIDProps} params - An object containing the tag for logging and the ID of the report to purchase.
 * @returns {Promise<ReportPurchaseDefaultApiResponse>} - A promise that resolves to an object containing purchase information.
 * 
 * @example
 * async function purchase() {
 *   const purchaseInfo = await purchaseReportUsingPayment({ tag: 'ReportPurchase', id: '12345' });
 *   console.log('Purchase information:', purchaseInfo);
 * }
 */
export async function getReportPurchaseIntent({ tag, id }: FetchDataByIDProps): Promise<{ success: boolean, message: string; hasAccess: boolean, url: string, paymentIntent: StripePaymentIntentResult}>{
    try {
        const result = await endpoints.purchaseReportUsingPayment({ tag, id })
        if (result && result.success && result.data) {
            const { data } = result;
            if ('amount' in data && 'clientSecret' in data && 'metadata' in data) {
                return { success: true, message: 'Report purchase link generated.', hasAccess: false, url: '', paymentIntent: data };
            } else if ('creditsUsed' in data && 'url' in data) {
                return { success: true, message: 'Report purchase link generated.', hasAccess: true, url: data.url, paymentIntent: { amount: 0, clientSecret: '', metadata: {} } };
            } else {
                logError('Error fetching report purchase intent.', { id, result }, tag);
                return { success: false, message: 'Error fetching report purchase intent.', hasAccess: false, url: '', paymentIntent: { amount: 0, clientSecret: '', metadata: {} } };
            }
        }
        logError('Error fetching report purchase intent.', { id, result }, tag)
        return { success: false, message: 'Error fetching report purchase intent.', hasAccess: false, url: '', paymentIntent: { amount: 0, clientSecret: '', metadata: {} } }
    } catch (error) {
        logError(
            'Unable to fetch report purchase intent.',
            { id, error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return { success: false, message: 'Error fetching report purchase intent.', hasAccess: false, url: '', paymentIntent: { amount: 0, clientSecret: '', metadata: {} } }
    }
}

/**
 * Updates the purchase status of a report.
 * 
 * @async
 * @function updateReportPurchaseStatus
 * @param {FetchDataByIDProps} params - An object containing the tag for logging and the ID of the report to update.
 * @returns {Promise<{ success: boolean, message: string }>} - A promise that resolves to an object containing the update status.
 * 
 * @example
 * async function updateStatus() {
 *   const updateInfo = await updateReportPurchaseStatus({ tag: 'ReportUpdate', id: '12345' });
 *   console.log('Update information:', updateInfo);
 * }
 */
export async function updateReportPurchaseStatus({ tag, id, data }: FetchDataByIDProps & { data: {
    status: 'paid' | 'failed',
    paymentId: string
}}): Promise<{ success: boolean, message: string, url?: string }> {
    try {
        const result = await endpoints.updateReportPurchaseStatus({ tag, id, data })
        if (result.success && result.data) {
            return { success: true, message: 'Report purchase status updated successfully.', url: result.data.url? result.data.url : '' }
        }
        logError('Error updating report purchase status.', { id, result }, tag)
        return { success: false, message: 'Error updating report purchase status.' }
    } catch (error) {
        logError(
            'Unable to update report purchase status.',
            { id, error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return { success: false, message: 'Error updating report purchase status.' }
    }
}

/**
 * Fetches chart data for a specific report by its ID.
 * 
 * @async
 * @function getReportChartData
 * @param {FetchDataByIDProps} params - An object containing the tag for logging and the ID of the report to fetch chart data for.
 * @returns {Promise<LegacyChart | null>} - A promise that resolves to the fetched chart data or `null` if not found.
 * 
 * @example
 * async function loadChartData() {
 *   const chartData = await getReportChartData({ tag: 'ReportChartFetch', id: '12345' });
 *   console.log('Fetched chart data:', chartData);
 * }
 */
export async function getReportChartData({ tag, id }: FetchDataByIDProps): Promise<LegacyChart | null> {
    try {
        const result = await endpoints.fetchReportChart({ tag, id })
        if (result.success && result.data) {
            return result.data
        }
        logError('Error fetching report chart data.', { id, result }, tag)
        return null;
    } catch (error) {
        logError(
            'Unable to retrieve report chart data.',
            { id, error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return null;
    }
}

/**
 * Fetches similar reports based on shared tags with the original report.
 * 
 * @async
 * @function getSimilarReports
 * @param {FetchDataByIDProps} params - An object containing the tag for logging and the ID of the original report.
 * @returns {Promise<SimilarReport[]>} - A promise that resolves to an array of similar reports.
 * 
 * @example
 * async function loadSimilarReports() {
 *   const similarReports = await getSimilarReports({ tag: 'SimilarReportFetch', id: 'ef58cfd0-76d8-43f1-aeed-1c916b56bece' });
 *   console.log('Fetched similar reports:', similarReports);
 * }
 */
export async function getSimilarReports({ tag, id }: FetchDataByIDProps): Promise<SimilarReport[]> {
    try {
        const result = await endpoints.fetchSimilarReports({ tag, id })
        if (result.success && result.data) {
            return result.data
        }
        logError('Error fetching similar reports.', { id, result }, tag)
        return []
    } catch (error) {
        logError(
            'Unable to retrieve similar reports.',
            { id, error: error instanceof Error ? error.message : 'Unknown error' },
            tag
        )
        return []
    }
}

/**
 * Provides a default set of report categories for use when fetching categories fails.
 * 
 * @function defaultCategories
 * @returns {ReportCategory[]} - An array of default report categories.
 */
function defaultCategories(): ReportCategory[] {
    return zeroneReportCategories;
}